/**
 * Normalized ↔ preview-pixel coordinate conversion for subtitle layers.
 * Preview scale is independent of export resolution (1080p reference for fonts).
 */

import { SUBTITLE_EXPORT_REF_HEIGHT } from '@/lib/videoSubtitlePreview';

const DPR = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

export function getPreviewDevicePixelRatio() {
  return Math.min(3, Math.max(1, DPR));
}

/** Pixel rect of a normalized layer inside a frame (top-left origin). */
export function normalizedLayerToPixelRect(layer, frameW, frameH) {
  const fw = Math.max(1, Number(frameW) || 1);
  const fh = Math.max(1, Number(frameH) || 1);
  const w = Math.max(8, layer.width * fw);
  const h = Math.max(8, layer.height * fh);
  const cx = layer.x * fw;
  const cy = layer.y * fh;
  return {
    left: cx - w / 2,
    top: cy - h / 2,
    width: w,
    height: h,
    centerX: cx,
    centerY: cy,
    rotation: Number(layer.rotation) || 0,
  };
}

/** Pointer position inside frame → normalized center. */
export function pointerToNormalizedLayerCenter(clientX, clientY, frameRect) {
  const fw = Math.max(1, frameRect.width);
  const fh = Math.max(1, frameRect.height);
  const x = (clientX - frameRect.left) / fw;
  const y = (clientY - frameRect.top) / fh;
  return { x, y };
}

/** Normalized center → export pixel position at target resolution. */
export function normalizedToExportPixels(layer, exportW, exportH) {
  const ew = Math.max(1, Number(exportW) || 1920);
  const eh = Math.max(1, Number(exportH) || 1080);
  const rect = normalizedLayerToPixelRect(layer, ew, eh);
  return {
    ...rect,
    exportFontPx: Math.round(
      (layer.height * eh) * (Number(layer.fontSize) || 22) / (SUBTITLE_EXPORT_REF_HEIGHT * 0.14),
    ),
  };
}

/** Snap normalized value to grid (e.g. 0.05). */
export function snapNormalized(value, grid = 0.05) {
  const g = Math.max(0.01, Number(grid) || 0.05);
  return Math.round(value / g) * g;
}

export function snapLayerValues(layer, grid = 0.05) {
  return {
    ...layer,
    x: snapNormalized(layer.x, grid),
    y: snapNormalized(layer.y, grid),
    width: snapNormalized(layer.width, grid),
    height: snapNormalized(layer.height, grid),
  };
}

/** Keep layer inside frame bounds (hard clip, not safe area). */
export function clampLayerInsideFrame(layer, bounds = {}) {
  const maxW = Number(bounds.maxWidth) > 0 ? Number(bounds.maxWidth) : 0.98;
  const maxH = Number(bounds.maxHeight) > 0 ? Number(bounds.maxHeight) : 0.45;
  const minW = Number(bounds.minWidth) > 0 ? Number(bounds.minWidth) : 0.08;
  const minH = Number(bounds.minHeight) > 0 ? Number(bounds.minHeight) : 0.06;
  const w = Math.min(maxW, Math.max(layer.width, minW));
  const h = Math.min(maxH, Math.max(layer.height, minH));
  const freePosition = bounds.positionMode === 'free';
  const minX = freePosition ? 0 : w / 2;
  const maxX = freePosition ? 1 : 1 - w / 2;
  const minY = freePosition ? 0 : h / 2;
  const maxY = freePosition ? 1 : 1 - h / 2;
  return {
    ...layer,
    width: w,
    height: h,
    x: Math.min(maxX, Math.max(minX, layer.x)),
    y: Math.min(maxY, Math.max(minY, layer.y)),
  };
}

/**
 * Keep the subtitle box inside the video frame.
 * Safe-area insets are visual guides only (see SafeAreaGuide) — not a drag limit.
 */
export function applyLayerConstraints(layer, aspectRatio, options = {}) {
  const { snap = false, snapGrid = 0.05 } = options;
  const bounds = {
    minWidth: options.minWidth,
    minHeight: options.minHeight,
    maxWidth: options.maxWidth,
    maxHeight: options.maxHeight,
    positionMode: options.positionMode,
  };
  let next = clampLayerInsideFrame(layer, bounds);
  if (snap) next = snapLayerValues(next, snapGrid);
  return next;
}
